import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { getDocChatStreamConversation } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import {
  readStoredComposerDraftForSession,
  resolveAgenticChatComposerDraft,
} from 'features/knowledgeDocumentViewer/utils/agenticChatComposerDraft'
import { getAgenticConversationKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import type { DocChatPreview } from 'features/knowledgeDocumentViewer/utils/docChatPreview'

function inFlightConversationForSession(
  session: RecordedAgenticChatSession
): DocChatConversation | undefined {
  return (
    getDocChatStreamConversation(session.key) ??
    (session.conversation.id
      ? getDocChatStreamConversation(session.conversation.id)
      : undefined) ??
    (session.conversation.pendingSessionKey
      ? getDocChatStreamConversation(session.conversation.pendingSessionKey)
      : undefined)
  )
}

function conversationWithSessionIdentity(
  conversation: DocChatConversation,
  sessionKey: string
): DocChatConversation {
  if (
    conversation.id !== undefined ||
    conversation.pendingSessionKey !== undefined
  ) {
    return conversation
  }
  return { ...conversation, pendingSessionKey: sessionKey }
}

function tabMatchesHistorySession(
  tab: DocumentTab,
  sessionKey: string,
  tabId?: string
): boolean {
  return (
    (tabId !== undefined && tab.id === tabId) ||
    getAgenticConversationKey(tab) === sessionKey
  )
}

function mapMatchingOpenTabs(
  openTabs: DocumentTab[],
  sessionKey: string,
  tabId: string | undefined,
  updateTab: (tab: DocumentTab) => DocumentTab
): DocumentTab[] {
  let changed = false
  const nextTabs = openTabs.map((tab) => {
    if (!tabMatchesHistorySession(tab, sessionKey, tabId)) return tab
    const next = updateTab(tab)
    if (next === tab) return tab
    changed = true
    return next
  })
  return changed ? nextTabs : openTabs
}

export async function selectAgenticChatHistorySession({
  activateWorkspaceTab,
  hydrateSession,
  openAgenticConversation,
  openTabId,
  session,
  siteId,
}: {
  activateWorkspaceTab: (tabId: string) => void
  hydrateSession: (
    session: RecordedAgenticChatSession
  ) => Promise<DocChatConversation | null>
  openAgenticConversation: (options: {
    composerDraft?: string
    conversation: DocChatConversation
    preview?: DocChatPreview
    siteId: string
    title: string
  }) => void
  openTabId?: string
  session: RecordedAgenticChatSession
  siteId: string | undefined
}) {
  if (openTabId) {
    activateWorkspaceTab(openTabId)
    return
  }
  if (!siteId) return
  const hydratedConversation =
    inFlightConversationForSession(session) ??
    (session.isHydrated === false
      ? await hydrateSession(session)
      : session.conversation)
  if (!hydratedConversation) return
  const conversation = conversationWithSessionIdentity(
    hydratedConversation,
    session.key
  )
  const preview = session.preview
  const composerDraft = resolveAgenticChatComposerDraft({
    sessionDraft: session.composerDraft,
    storedDraft: readStoredComposerDraftForSession({
      conversationId: conversation.id,
      sessionKey: session.key,
      siteId,
    }),
  })
  openAgenticConversation({
    conversation,
    siteId,
    title: session.title,
    ...(composerDraft === '' ? {} : { composerDraft }),
    ...(preview === undefined ? {} : { preview }),
  })
}

export async function dismissAgenticChatHistorySession({
  closeWorkspaceTab,
  onPersist,
  onRemoveLocalSession,
  openTabId,
  sessionKey,
}: {
  closeWorkspaceTab: (tabId: string) => boolean
  onPersist: (sessionKey: string) => Promise<boolean>
  onRemoveLocalSession: (sessionKey: string) => void
  openTabId?: string
  sessionKey: string
}) {
  const persisted = await onPersist(sessionKey)
  if (!persisted) return
  if (openTabId) closeWorkspaceTab(openTabId)
  onRemoveLocalSession(sessionKey)
}

export async function archiveAgenticChatHistorySession({
  closeWorkspaceTab,
  onArchiveSession,
  onRemoveLocalSession,
  openTabId,
  sessionKey,
}: {
  closeWorkspaceTab: (tabId: string) => boolean
  onArchiveSession: (sessionKey: string) => Promise<boolean>
  onRemoveLocalSession: (sessionKey: string) => void
  openTabId?: string
  sessionKey: string
}) {
  await dismissAgenticChatHistorySession({
    closeWorkspaceTab,
    onPersist: onArchiveSession,
    onRemoveLocalSession,
    openTabId,
    sessionKey,
  })
}

export function applyAgenticChatTabTitle({
  openTabs,
  sessionKey,
  tabId,
  title,
}: {
  openTabs: DocumentTab[]
  sessionKey: string
  tabId?: string
  title: string
}): DocumentTab[] {
  return mapMatchingOpenTabs(openTabs, sessionKey, tabId, (tab) =>
    tab.customLabel === title ? tab : { ...tab, customLabel: title }
  )
}

export function applyDocumentChatConversationToOpenTabs({
  openTabs,
  sessionKey,
  tabId,
  conversation,
}: {
  openTabs: DocumentTab[]
  sessionKey: string
  tabId?: string
  conversation: DocChatConversation
}): DocumentTab[] {
  return mapMatchingOpenTabs(openTabs, sessionKey, tabId, (tab) => {
    const item = tab.history[tab.currentIndex]
    if (item?.type !== 'search') return tab
    const docChat = item.searchState.docChat
    if (!docChat || docChat.conversation === conversation) return tab
    const nextHistory = [...tab.history]
    nextHistory[tab.currentIndex] = {
      ...item,
      searchState: {
        ...item.searchState,
        docChat: {
          ...docChat,
          conversation,
          isHydratedHistory: true,
        },
      },
    }
    return { ...tab, history: nextHistory }
  })
}

export function renameAgenticChatHistorySession({
  onRenameSession,
  openTabId,
  renameWorkspaceTab,
  sessionKey,
  title,
}: {
  onRenameSession: (sessionKey: string, title: string) => void
  openTabId?: string
  renameWorkspaceTab: (args: {
    sessionKey: string
    tabId?: string
    title: string
  }) => void
  sessionKey: string
  title: string
}) {
  const nextTitle = title.trim()
  if (nextTitle === '') return
  onRenameSession(sessionKey, nextTitle)
  renameWorkspaceTab({
    sessionKey,
    tabId: openTabId,
    title: nextTitle,
  })
}
